import {Tab} from "@headlessui/react";
import {useAppSelector} from "@hooks/redux-hooks/hooks";
import {selectAzienda, selectLavoratore, selectToken, selectUser} from "@commons/core.slice";
import AziendaNavContainer from "../../features/azienda/AziendaNavContainer";
import React, {useState} from "react";
import {AziendaRecord} from "@commons/mapping/AziendaRecord";
import {LavoratoreBar} from "../../features/lavoratore/LavoratoreBar";
import BasicBadge from "@commons/components/BasicBadge";
import {useListDocuments} from "@commons/services/lavoratori/lavoratori.documenti.queries";
import {DocumentoParams} from "@commons/services/lavoratori/lavoratori.documenti.api";
import {DocType, UniversalDocRecord} from "@commons/mapping/UniversalDocRecord";
import * as Buttons from "@components/dataTable/buttons";
import DTTable from "../../components/dataTable/Table";
import moment from "moment";
import Loading from "@components/dataTable/Loading";
import {SezioneDocsBar} from "@components/SezioneDocsBar";
import {UserCRUDRecord} from "@commons/mapping/UserCRUDRecord";
import {CreateButton, useEditor} from "../../core/editor";
import {PubblicazioneWizard} from "@commons/mapping/PubblicazioneWizard";
import UploadDocAltro from "./Lavoratori.documenti.UploadDocAltro";
import TooltipEx from "@components/Tooltip";
import SezioneDocsTable, {DescrizioneDoc} from "./Lavoratori.documenti.sezioneTable";
import {useQueryClient} from "@tanstack/react-query";
import {DownloadFile, Preview} from "@components/dataTable/buttons/DownloadFile";
import {IconBuildingSkyscraper, IconCurrencyEuro, IconEyeOff, IconFileDescription, IconGavel, IconMail, IconMailOpened, IconPin} from "@tabler/icons-react";

const LavoratoriDocumentiPage: React.FC<{}> = () => {

    const azienda: AziendaRecord | null = useAppSelector(selectAzienda);
    const [sezioneSel, setSezioneSel] = useState<DocType | ''>('');
    const [URI, setNextURI] = useState<string | null>(null);

    const {resource, create, cancel} = useEditor<PubblicazioneWizard>();

    const lavoratore = useAppSelector(selectLavoratore)
    const idDip = lavoratore !== null ? lavoratore.id : 0;
    const token = useAppSelector(selectToken);

    const anteprimaParams: DocumentoParams = {
        start: 0,
        length: 0,
        idDip: idDip,
        sezione: '',
        anteprima: true, // N.B. serve per farsi restituire solo i 5 documenti più recenti
        view: 'Grid',
        mode: 'DataTables',
    } as DocumentoParams;
    const sezioneParams = {...anteprimaParams, sezione: sezioneSel, anteprima: false};

    const {refetch, queryKey, isLoading, error: errDocBadges, data: docsAndBadges, isRefetching} = useListDocuments(anteprimaParams);

    function getStyleIcon(sezione: DocType | null) {
        switch (sezione) {
            case 'CEDOLINI':
                return 'cedoliniIconColor';
            case 'CERTIFICAZIONI':
                return 'certificazioniIconColor';
            case 'COMUNICAZIONI':
                return 'comunicazioniIconColor';
            case 'ALTRI':
                return 'altriIconColor';
            default :
                return '';
        }
    }

    let mancaAbilitazione = false;
    // @ts-ignore
    if (errDocBadges && errDocBadges.response) { // @ts-ignore
        if (errDocBadges.response.status === 401)
            mancaAbilitazione = true;
    }

    const user: UserCRUDRecord | null = useAppSelector(selectUser); // User corrente
    const isEmployee = user?.userClass === 'EMPLOYEE';

    const queryClient = useQueryClient();
    const reloadAnteprima = () => {
        queryClient.invalidateQueries({queryKey});
    }

    return (
        <>
            <AziendaNavContainer>
                {azienda && <>
                    <LavoratoreBar azienda={azienda}>
                        <Loading loading={isLoading || isRefetching}>
                            {/* Se l'utente PUO' accedere ai documenti del lavoratore selezionato, si trova sulla pagina principale e ci sono i dati */}
                            {sezioneSel === '' && docsAndBadges && !mancaAbilitazione && <div className="overflow-hidden rounded-md bg-white shadow px-4 py-5 sm:p-6">
                                <div className="docBoxContainer">
                                    <a id="CEDOLINI" className="static" onClick={() => setSezioneSel('CEDOLINI')}>
                                        <div className="docBox centerTextIcon cedolini">
                                            <span className="inlineTextIcon">Cedolini paga&nbsp;<IconCurrencyEuro/></span>
                                            {docsAndBadges && <BasicBadge label={docsAndBadges.CEDOLINI} type={"custom"} textClass="badgeDocBox"
                                                                          customStyle={{bg: 'bg-red-500', text: 'text-white !text-xs'}}></BasicBadge>}
                                        </div>
                                    </a>
                                    <a id="CERTIFICAZIONI" className="static" onClick={() => setSezioneSel('CERTIFICAZIONI')}>
                                        <div className="docBox centerTextIcon certificazioni">
                                            <span className="inlineTextIcon">Certificazioni&nbsp;<IconGavel/></span>
                                            {docsAndBadges && <BasicBadge label={docsAndBadges.CERTIFICAZIONI} type={"custom"} textClass="badgeDocBox"
                                                                          customStyle={{bg: 'bg-red-500', text: 'text-white !text-xs'}}></BasicBadge>}
                                        </div>
                                    </a>

                                    <div className="docBoxContainer">
                                        <a id="COMUNICAZIONI_AZ" className="static" onClick={() => setSezioneSel('COMUNICAZIONI')}>
                                            <div className="docBox centerTextIcon comunicazioni px-3">
                                                <span className="text-center">Comunicazioni aziendali&nbsp;</span><span><IconBuildingSkyscraper className="h-6 w-6"
                                                                                                                                                strokeWidth={1.5}/></span>
                                                {docsAndBadges && <BasicBadge label={docsAndBadges.COMUNICAZIONI} type={"custom"} textClass="badgeDocBox"
                                                                              customStyle={{bg: 'bg-red-500', text: 'text-white !text-xs'}}></BasicBadge>}
                                            </div>
                                        </a>
                                        <a id="DOC_AZ" className="static" onClick={() => setSezioneSel('DOC_PUBBLICI')}>
                                            <div className="docBox centerTextIcon doc_pubblici">
                                                <span className="inlineTextIcon">Bacheca&nbsp;<IconPin/></span>
                                            </div>
                                        </a>
                                    </div>

                                    <a id="ALTRI_DOC" className="static" onClick={() => setSezioneSel('ALTRI')}>
                                        <div className="docBox centerTextIcon altri">
                                            <span className="inlineTextIcon">Altri documenti&nbsp;<IconFileDescription className="h-6 w-6" strokeWidth={1.5}/></span>
                                            {docsAndBadges && <BasicBadge label={docsAndBadges.ALTRI} type={"custom"} textClass="badgeDocBox"
                                                                          customStyle={{bg: 'bg-red-500', text: 'text-white !text-xs'}}></BasicBadge>}
                                        </div>
                                    </a>
                                </div>

                                <div className="w-full text-xs text-red-600 text-right mt-2">
                                    N.B. Il numero di documenti indicato per ciascuna sezione (es. <span
                                    className="rounded-full px-1.5 py-0.5 bg-red-500 text-white">20</span>) &egrave; riferito a quelli non letti dal lavoratore.
                                </div>

                                <div className="font-semibold text-gray-900 max-sm:text-md max-md:text-lg md:text-xl mt-5">Ultimi documenti pubblicati</div>
                                <DTTable<UniversalDocRecord> dtSchema={docsAndBadges}
                                                             header={<>
                                                                 <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                                                     Descrizione
                                                                 </th>
                                                                 <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                                                     Data pubblicazione
                                                                 </th>
                                                                 <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                                                     Data lettura
                                                                 </th>
                                                                 <th scope="col" className="py-3.5 w-14"/>
                                                             </>}

                                                             rows={({item, index, isMobile, currentRow, register}) => (
                                                                 <tr {...register(item.idFile!, index, item.URI === URI)}>
                                                                     <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-500 sm:pl-6">
                                                                                 <span className="inline-flex">
                                                                                     {!item.dtLettura &&
                                                                                         <IconMail className={'h-5 w-5 ' + getStyleIcon(item.sezione)} strokeWidth={1.5}/>}
                                                                                     {item.dtLettura &&
                                                                                         <IconMailOpened className={'h-5 w-5 ' + getStyleIcon(item.sezione)} strokeWidth={1.5}/>}
                                                                                     &nbsp;&nbsp;
                                                                                     <DescrizioneDoc item={item}/>
                                                                                 </span>
                                                                     </td>
                                                                     <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-500 sm:pl-6 inline-flex">
                                                                         {item.dtPubblicazione && moment(item.dtPubblicazione).format('DD/MM/YYYY, HH:mm:ss')}&nbsp;
                                                                         {(!item.visibleAzienda || !item.visible) && <TooltipEx tooltip={
                                                                             <>
                                                                                 {!item.visibleAzienda && 'Documento non visibile né all\'azienda né al lavoratore'}
                                                                                 {item.visibleAzienda && !item.visible && 'Documento non visibile al lavoratore'}
                                                                             </>
                                                                         }>
                                                                             <div className={`p-0 m-0 ${!item.visibleAzienda && 'text-red-600'}`}><IconEyeOff size={20}/></div>
                                                                         </TooltipEx>}
                                                                     </td>

                                                                     <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-500 sm:pl-6">
                                                                         {item.dtLettura && moment(item.dtLettura).format('DD/MM/YYYY, HH:mm:ss')}
                                                                     </td>

                                                                     <td className="whitespace-nowrap py-2 text-sm font-medium">
                                                                         <Buttons.Visibility visible={isMobile || index === currentRow}>
                                                                             {/*http://fpnet.local/Docs2022/doc/m-files/contratto/3562-0/file/3289782-3-3288820?auth_token=2853b3649a04a37a9674a58e033b9ca2&contentDisposition=inline*/}
                                                                             <Preview srcFile={item.URI} onFulfilled={reloadAnteprima}/>
                                                                             {/*http://fpnet.local/Docs2022/doc/m-files/contratto/3562-0/file/3289782-3-3288820?auth_token=dbbbda55ddbdb870d7bbfbf0bf5431c8&nowId=1689083364400&isValid=true&type=attachment*/}
                                                                             <DownloadFile srcFile={item.URI} onFulfilled={reloadAnteprima}/>
                                                                         </Buttons.Visibility>
                                                                     </td>
                                                                 </tr>)
                                                             }
                                />
                            </div>}

                            {/* Se l'utente NON puo' accedere ai documenti del lavoratore selezionato*/}
                            {mancaAbilitazione && <div className="overflow-hidden rounded-md bg-white shadow px-4 py-5 sm:p-6">
                                <h3 className="text-red-600">Non puoi accedere ai documenti del lavoratore selezionato!</h3>
                            </div>}

                            {/* ALL'INTERNO DI UNA SEZIONE SELEZIONATA */}
                            {sezioneSel !== '' && !mancaAbilitazione &&
                                <div className="overflow-hidden rounded-md bg-white shadow px-4 py-5 sm:p-6">
                                    <SezioneDocsBar sezioneDoc={sezioneSel} setSezioneSel={() => {
                                        setSezioneSel('');
                                        if (sezioneSel === 'ALTRI') {
                                            cancel()
                                        }
                                    }}/>

                                    {/* UPLOAD DOCUMENTO IN SEZIONE "ALTRO" */}
                                    {sezioneSel === 'ALTRI' && <div>
                                        {/*::hasGrant4View('policies.Docs2022.doc.m_files.contratto.idDip.file','$gPost')*/}
                                        <div className="w-full text-right pb-5">
                                            <CreateButton<PubblicazioneWizard> create={create} label="Documento"/>
                                        </div>
                                        <Tab.Group>
                                            <Tab.Panels className="tab-panels">
                                                <Tab.Panel unmount={false}>
                                                    {resource && <UploadDocAltro idDip={idDip} resource={resource} onCancel={cancel}
                                                                                 params={sezioneParams}/>}
                                                </Tab.Panel>
                                            </Tab.Panels>
                                        </Tab.Group>
                                    </div>}

                                    <SezioneDocsTable sezioneParams={sezioneParams} isEmployee={isEmployee} anteprimaQueryKey={queryKey}/>
                                </div>
                            }
                        </Loading>
                    </LavoratoreBar>
                </>}
            </AziendaNavContainer>
        </>
    )
}
export default LavoratoriDocumentiPage;